import React, { useState, useEffect } from 'react';
import { Stars, Moon, Sun, Heart, Sparkles, Calendar, Users, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../AuthContext';
import { useToast } from '../hooks/use-toast';

const Astrology = () => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('daily');
  const [dailyReading, setDailyReading] = useState(null);
  const [zodiacInfo, setZodiacInfo] = useState(null);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [compatibility, setCompatibility] = useState(null);
  const [partnerBirthDate, setPartnerBirthDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFocus, setSelectedFocus] = useState('general');

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Fetch user's astrology data when component mounts
  useEffect(() => {
    if (user?.date_of_birth) {
      console.log('User DOB found:', user.date_of_birth);
      loadAstrologyData();
    } else {
      console.log('No user DOB found');
      setError('Please add your date of birth in Profile Settings to get personalized astrology insights.');
    }
  }, [user]);

  const loadAstrologyData = async () => {
    setLoading(true);
    setError('');
    
    try {
      await Promise.all([
        fetchZodiacInfo(user.date_of_birth),
        fetchCurrentSeason(),
        fetchDailyReading()
      ]);
    } catch (error) {
      console.error('Error loading astrology data:', error);
      setError('Unable to load your astrology insights. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchZodiacInfo = async (birthDate) => {
    try {
      console.log('Fetching zodiac info for:', birthDate);
      const response = await fetch(`${API_BASE_URL}/api/astrology/zodiac-info/${birthDate}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setZodiacInfo(data);
        console.log('Zodiac info loaded:', data);
      } else {
        const errorText = await response.text();
        throw new Error(`Zodiac API failed: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error fetching zodiac info:', error);
      throw error;
    }
  };

  const fetchCurrentSeason = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/astrology/current-season`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentSeason(data);
        console.log('Current season loaded:', data);
      } else {
        const errorText = await response.text();
        throw new Error(`Season API failed: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error fetching current season:', error);
      throw error;
    }
  };

  const fetchDailyReading = async () => {
    if (!user?.date_of_birth) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/astrology/daily-reading`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          birth_date: user.date_of_birth,
          focus_area: selectedFocus
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setDailyReading(data);
        console.log('Daily reading loaded:', data);
      } else {
        const errorText = await response.text();
        throw new Error(`Daily reading API failed: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error fetching daily reading:', error);
      throw error;
    }
  };

  const fetchCompatibility = async () => {
    if (!user?.date_of_birth || !partnerBirthDate) {
      toast({
        title: "Missing Information",
        description: "Please enter your partner's birth date",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/astrology/compatibility`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_birth_date: user.date_of_birth,
          partner_birth_date: partnerBirthDate
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCompatibility(data);
        toast({
          title: "✨ Compatibility Analysis Ready!",
          description: "Your cosmic connection insights are here",
          duration: 3000,
        });
      } else {
        const errorText = await response.text();
        throw new Error(`Compatibility API failed: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error fetching compatibility:', error);
      toast({
        title: "Oops! Something's missing. Let's try again 💕",
        description: "Unable to analyze compatibility",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshDailyReading = () => {
    if (user?.date_of_birth) {
      fetchDailyReading();
      toast({
        title: "🌟 Refreshing Your Cosmic Insights",
        description: "Getting fresh guidance from the stars",
        duration: 2000,
      });
    }
  };

  const handleFocusChange = (newFocus) => {
    setSelectedFocus(newFocus);
    if (user?.date_of_birth && dailyReading) {
      fetchDailyReading(); // Refresh with new focus
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric'
    });
  };

  // Handle missing DOB
  if (!user?.date_of_birth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-rose-100 pb-24 pt-20">
        <div className="max-w-lg mx-auto px-4 text-center">
          <Card className="p-8 bg-white/80 backdrop-blur-sm border-purple-200">
            <div className="text-6xl mb-6">🔮</div>
            <h1 className="text-2xl font-serif font-bold text-gray-900 mb-4">Astrology Insights</h1>
            <p className="text-gray-600 mb-6 font-sans">
              To get personalized cosmic guidance, we need to know when you were born.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-yellow-800 text-sm font-sans">
                💡 <strong>Why we need your birth date:</strong> Your zodiac sign, moon phase, and planetary positions 
                at birth create your unique cosmic blueprint for self-love and growth.
              </p>
            </div>
            <Button 
              asChild
              className="bg-rose-600 hover:bg-rose-700 text-white rounded-full font-sans"
            >
              <a href="/app/profile">
                🌟 Add Birth Date in Profile
              </a>
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-rose-100 pb-24 pt-20">
      <div className="max-w-6xl mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Stars className="w-8 h-8 text-purple-500 mr-3" />
            <h1 className="text-4xl font-serif font-bold text-gray-800">Astrology Insights</h1>
          </div>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto font-sans">
            Discover your cosmic blueprint and daily guidance for love, growth, and transformation
          </p>
        </div>

        {/* Error State */}
        {error && (
          <Card className="p-6 bg-red-50 border-red-200 mb-6 text-center">
            <div className="text-4xl mb-4">🌙</div>
            <p className="text-red-800 font-sans">{error}</p>
            <Button 
              onClick={loadAstrologyData}
              className="mt-4 bg-rose-600 hover:bg-rose-700 text-white font-sans"
            >
              Try Again
            </Button>
          </Card>
        )}

        {/* Loading State */}
        {loading && (
          <Card className="p-8 bg-white/80 backdrop-blur-sm border-purple-200 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500 mx-auto mb-4" />
            <h3 className="text-lg font-serif font-semibold text-gray-900 mb-2">
              Reading the Stars...
            </h3>
            <p className="text-gray-600 font-sans">
              Gathering your personalized cosmic insights ✨
            </p>
          </Card>
        )}

        {/* Main Content */}
        {!loading && !error && zodiacInfo && (
          <>
            {/* Zodiac Overview */}
            <Card className="p-6 bg-gradient-to-r from-purple-50 to-rose-50 border-purple-200 mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-serif font-bold text-gray-900">
                    Your Zodiac: {zodiacInfo.zodiac_sign} ✨
                  </h2>
                  <p className="text-gray-600 font-sans">
                    Born {formatDate(user.date_of_birth)} • {zodiacInfo.element} {zodiacInfo.modality}
                  </p>
                </div>
                <div className="text-6xl">
                  {zodiacInfo.symbol || '⭐'}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-3 bg-white/60 rounded-lg">
                  <div className="text-sm font-medium text-gray-700 font-sans">Element</div>
                  <div className="font-bold text-purple-600 font-serif">{zodiacInfo.element}</div>
                </div>
                <div className="text-center p-3 bg-white/60 rounded-lg">
                  <div className="text-sm font-medium text-gray-700 font-sans">Quality</div>
                  <div className="font-bold text-purple-600 font-serif">{zodiacInfo.modality}</div>
                </div>
                <div className="text-center p-3 bg-white/60 rounded-lg">
                  <div className="text-sm font-medium text-gray-700 font-sans">Ruling Planet</div>
                  <div className="font-bold text-purple-600 font-serif">{zodiacInfo.ruling_planet}</div>
                </div>
              </div>
            </Card>

            {/* Navigation Tabs */}
            <div className="flex justify-center mb-8">
              <div className="flex space-x-1 bg-white/70 backdrop-blur-sm p-1 rounded-lg border border-purple-200">
                {[
                  { id: 'daily', name: 'Daily Reading', icon: Sun },
                  { id: 'compatibility', name: 'Love Compatibility', icon: Heart }
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center space-x-2 px-6 py-3 rounded-md font-medium transition-all font-sans ${
                        activeTab === tab.id
                          ? 'bg-rose-600 text-white shadow-md'
                          : 'text-gray-600 hover:text-rose-600 hover:bg-rose-50'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{tab.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Daily Reading Tab */}
            {activeTab === 'daily' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                
                {/* Focus Area Selection */}
                <div className="lg:col-span-1">
                  <Card className="p-6 bg-white/70 backdrop-blur-sm border-purple-200 sticky top-24">
                    <h3 className="font-serif font-semibold text-gray-900 mb-4">Focus Areas</h3>
                    <div className="space-y-2">
                      {[
                        { id: 'general', name: 'General Guidance', icon: '✨' },
                        { id: 'love', name: 'Love & Relationships', icon: '💕' },
                        { id: 'career', name: 'Career & Purpose', icon: '🌟' },
                        { id: 'personal_growth', name: 'Personal Growth', icon: '🌿' }
                      ].map((focus) => (
                        <button
                          key={focus.id}
                          onClick={() => handleFocusChange(focus.id)}
                          className={`w-full text-left p-3 rounded-lg transition-colors border font-sans ${
                            selectedFocus === focus.id
                              ? 'bg-rose-100 border-rose-300 text-rose-800'
                              : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <span className="mr-2">{focus.icon}</span>
                          {focus.name}
                        </button>
                      ))}
                    </div>
                    
                    <Button
                      onClick={refreshDailyReading}
                      variant="outline"
                      className="w-full mt-4 border-rose-300 text-rose-600 hover:bg-rose-50 font-sans"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      New Reading
                    </Button>
                  </Card>
                </div>

                {/* Daily Reading Content */}
                <div className="lg:col-span-2">
                  {dailyReading ? (
                    <Card className="p-6 bg-white/70 backdrop-blur-sm border-purple-200">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-serif font-bold text-gray-900">
                          Today's {selectedFocus.replace('_', ' ')} Reading
                        </h3>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500 font-sans">
                            {new Date().toLocaleDateString()}
                          </span>
                          <Stars className="w-4 h-4 text-purple-500" />
                        </div>
                      </div>

                      {/* Reading Content */}
                      <div className="space-y-6">
                        <div className="bg-gradient-to-r from-purple-50 to-rose-50 rounded-lg p-6">
                          <h4 className="font-serif font-semibold text-gray-900 mb-3">
                            Cosmic Guidance for {zodiacInfo.zodiac_sign} 🌟
                          </h4>
                          <p className="text-gray-800 leading-relaxed font-sans">
                            {dailyReading.content}
                          </p>
                        </div>

                        {/* Focus Areas */}
                        {dailyReading.focus_areas && dailyReading.focus_areas.length > 0 && (
                          <div className="bg-blue-50 rounded-lg p-4">
                            <h4 className="font-serif font-semibold text-blue-900 mb-3">Today's Focus 🎯</h4>
                            <div className="flex flex-wrap gap-2">
                              {dailyReading.focus_areas.map((area, index) => (
                                <span 
                                  key={index}
                                  className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-sans"
                                >
                                  {area}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Affirmation */}
                        {dailyReading.affirmation && (
                          <div className="bg-rose-50 rounded-lg p-4">
                            <h4 className="font-serif font-semibold text-rose-900 mb-3">Your Cosmic Affirmation 💖</h4>
                            <p className="text-rose-800 italic font-medium font-sans">
                              "{dailyReading.affirmation}"
                            </p>
                            <Button 
                              onClick={() => {
                                toast({
                                  title: "✨ Added to your Love Vault",
                                  description: "A reminder of your cosmic worth, always waiting for you 💕",
                                  duration: 3000,
                                });
                              }}
                              size="sm"
                              className="mt-3 bg-rose-500 hover:bg-rose-600 text-white rounded-full font-sans"
                            >
                              💕 Save to My Love Vault
                            </Button>
                          </div>
                        )}

                        {/* Growth Tip */}
                        {dailyReading.growth_tip && (
                          <div className="bg-green-50 rounded-lg p-4">
                            <h4 className="font-serif font-semibold text-green-900 mb-3">Growth Wisdom 🌱</h4>
                            <p className="text-green-800 font-sans">
                              {dailyReading.growth_tip}
                            </p>
                          </div>
                        )}
                      </div>
                    </Card>
                  ) : (
                    <Card className="p-8 bg-white/80 backdrop-blur-sm border-purple-200 text-center">
                      <div className="text-5xl mb-4">🌟</div>
                      <h3 className="text-xl font-serif font-semibold text-gray-900 mb-3">
                        Your Daily Reading Awaits
                      </h3>
                      <p className="text-gray-600 mb-6 font-sans">
                        Select a focus area to receive personalized cosmic guidance for your transformation journey.
                      </p>
                    </Card>
                  )}
                  
                  {/* Elegant Disclaimer */}
                  <div className="mt-6 text-center">
                    <p className="text-xs text-gray-500 font-sans italic">
                      ✨ For inspiration and self-reflection • Cosmic insights to support your growth journey 🌟
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Compatibility Tab */}
            {activeTab === 'compatibility' && (
              <div className="max-w-4xl mx-auto">
                <Card className="p-6 bg-white/70 backdrop-blur-sm border-purple-200">
                  <h3 className="text-xl font-serif font-bold text-gray-900 mb-6 text-center">
                    Love Compatibility Analysis 💕
                  </h3>
                  
                  <div className="max-w-md mx-auto mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2 font-sans">
                      Partner's Birth Date
                    </label>
                    <input
                      type="date"
                      value={partnerBirthDate}
                      onChange={(e) => setPartnerBirthDate(e.target.value)}
                      className="w-full p-3 border border-purple-200 rounded-lg focus:ring-purple-300 focus:border-purple-300"
                    />
                    <Button
                      onClick={fetchCompatibility}
                      disabled={!partnerBirthDate || loading}
                      className="w-full mt-4 bg-rose-600 hover:bg-rose-700 text-white rounded-full font-sans"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          💕 Analyze Our Connection
                        </>
                      )}
                    </Button>
                  </div>

                  {compatibility && (
                    <div className="space-y-6">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-purple-600 mb-2">
                          {compatibility.compatibility_score}%
                        </div>
                        <p className="text-gray-600 font-sans">Compatibility Score</p>
                      </div>
                      
                      <div className="bg-gradient-to-r from-purple-50 to-rose-50 rounded-lg p-6">
                        <h4 className="font-serif font-semibold text-gray-900 mb-3">Your Cosmic Connection 💫</h4>
                        <p className="text-gray-800 leading-relaxed font-sans">
                          {compatibility.analysis}
                        </p>
                      </div>
                    </div>
                  )}
                </Card>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Astrology;